import moment from 'moment';
import {
  IDiagnoseReferenceRanges,
  IDiagnoseResult,
  IDigitalHealthProfile,
} from '../SelectedSalesOrder/DigitalHealthProfileTab';

import { toast } from 'react-toastify';
import { DoctorLetterTypeId } from '../../../enums/enums';
import BiomarkerItem from './BiomarkerItem';
import BackButton from '../Buttons/BackButton';
import React from 'react';
import BiomarkerItemSmall from './BiomarkerItemSmall';
import BiomarkerItemVideoCall from './BiomarkerItemVideoCall';
import arrowRight from '../../../assets/icons/arrow-right.svg';
import downloadIcon from '../../../assets/icons/download-icon.svg';
import classes from './BiomarkerCard.module.css';
import { useTranslation } from '../../../hooks/useTranslations';

type Props = {
  dhp: IDigitalHealthProfile;
  onClick: () => void;
  expanded: boolean;
  onSelectDiagnoseResult: (
    diagnoseResult: IDiagnoseResult,
    product?: IDigitalHealthProfile
  ) => void;
  isVideoCall?: boolean;
};

const BiomarkerCard: React.FC<Props> = ({
  dhp,
  onClick,
  expanded,
  onSelectDiagnoseResult,
  isVideoCall,
}) => {
  const { translations } = useTranslation();

  const biomarkerNumberText = (): string => {
    if (!dhp.diagnose_result) return '';
    const numberOfResults = dhp.diagnose_result.length;
    if (numberOfResults === 1) return '1 Biomarker';
    if (numberOfResults === 2) return '2 Biomarkers';
    if (numberOfResults > 2) return `+${numberOfResults - 2} Biomarkers`;
    return '';
  };

  const biomarkerItemHeight =
    dhp.diagnose_result && dhp.diagnose_result.length === 1 ? '160px' : 'auto';

  const getDianosticReferenceRange = (
    propertyId: number,
    propertyValue: string
  ): IDiagnoseReferenceRanges | undefined => {
    if (!dhp.diagnose_reference_ranges) return undefined;
    if (dhp.diagnose_reference_ranges.length === 1) return dhp.diagnose_reference_ranges[0];
    const propertyValueNumber = Number(propertyValue);
    const diagnoseReferencesFiltered = dhp.diagnose_reference_ranges.filter(
      (drr) => drr.property_id === propertyId
    );
    for (const drr of diagnoseReferencesFiltered) {
      const propertyRanges = drr.property_range.slice(1, -1).split(',');
      if (
        propertyValueNumber >= Number(propertyRanges[0]) &&
        propertyValueNumber <= Number(propertyRanges[1])
      )
        return drr;
    }
  };

  const handleDownload = () => {
    const doctorAttachmentFound: any = dhp?.attachments?.find((att: any) =>
      DoctorLetterTypeId.includes(att.attachment_type_id)
    );

    if (doctorAttachmentFound && doctorAttachmentFound.link) {
      window.open(
        `${window.location.protocol}//${window.location.host}${doctorAttachmentFound?.link}`,
        '_blank'
      );
    } else {
      toast.error(translations('error.no_attachments').toString(), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  return (
    <div
      className={classes.container}
      onClick={onClick}
      style={{
        width: expanded ? '100%' : '360px',
        height: expanded ? 'auto' : '280px',
      }}
    >
      {!dhp?.diagnose_result || dhp.diagnose_result.length === 0 ? (
        <div className="emptyState">{translations('label.empty_state')}</div>
      ) : (
        <>
          <div className={classes.header}>
            <div className={classes.productName}>{dhp.product_name}</div>
            {expanded ? (
              <div className={classes.btnWrapp}>
                <div
                  className={classes.downloadBtn}
                  onClick={handleDownload}
                  style={{ cursor: 'pointer' }}
                >
                  <span>{translations('download')}</span>
                  <img src={downloadIcon} alt="download" />
                </div>
                <BackButton onClick={onClick} />
              </div>
            ) : (
              <div className={classes.bookingDate}>
                {moment(dhp.booking_date).format('DD.MM.YYYY')}
              </div>
            )}
          </div>
          <div className={classes.body}>
            {!expanded
              ? dhp.diagnose_result
                ?.slice(0, 2)
                .map((dh, index) => (
                  <BiomarkerItemSmall
                    key={index}
                    dhp={dhp}
                    biomarker={dh}
                    propertyName={dh.property_name}
                    diagnoseReferenceRange={getDianosticReferenceRange(
                      dh.property_id,
                      dh.property_value
                    )}
                    propertyValue={dh.property_value}
                    uom={dh.uom}
                    onSelectDiagnoseResult={() => onSelectDiagnoseResult(dh, dhp)}
                    height={biomarkerItemHeight}
                    small
                  />
                ))
              : dhp.diagnose_result?.map((dh, index) => {
                if (isVideoCall) {
                  return (
                    <BiomarkerItemVideoCall
                      key={index}
                      dhp={dhp}
                      biomarker={dh}
                      diagnoseReferenceRange={getDianosticReferenceRange(
                        dh.property_id,
                        dh.property_value
                      )}
                      onSelectDiagnoseResult={() => onSelectDiagnoseResult(dh, dhp)}
                    />
                  );
                }
                return (
                  <BiomarkerItem
                    key={index}
                    dhp={dhp}
                    biomarker={dh}
                    propertyName={dh.property_name}
                    diagnoseReferenceRange={getDianosticReferenceRange(
                      dh.property_id,
                      dh.property_value
                    )}
                    abbreviation={dh.property_short_abbreviation}
                    propertyValue={dh.property_value}
                    uom={dh.uom}
                    onSelectDiagnoseResult={() => onSelectDiagnoseResult(dh, dhp)}
                  />
                );
              })}
          </div>
          {!expanded && (
            <div className={classes.footer}>
              <div className={classes.biomarkerNumber}>{biomarkerNumberText()}</div>
              <img src={arrowRight} alt="arrowRight" />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BiomarkerCard;
